"use client";

import Link from "next/link";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { signIn } from "@/lib/actions/user.actions";
import { useToast } from "@/components/ui/use-toast";
import { HOME_REDIRECT } from "@/constants/message";
import { signInResponse } from "@/types/index";
import { useAuthContext } from "@/context/userContext";
import { useNotification } from "@/lib/firebase";
import { Eye, EyeOff } from "lucide-react"; // Import eye icons

const formSchema = z.object({
  email: z.string().email(),
  password: z.string(),
});

const AuthForm = ({ type }: { type: string }) => {
  const { user, setUser } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const { triggerReRegistration } = useNotification();
  const [showPassword, setShowPassword] = useState(false); // Move useState here
  const redirectToastShown = useRef(false);

  // Only allow internal, same-origin paths to avoid open-redirect attacks.
  const getSafeRedirect = () => {
    const target = searchParams.get("redirect");
    if (!target) return "/";
    if (!target.startsWith("/") || target.startsWith("//")) return "/";
    return target;
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    if (redirectToastShown.current) return;
    const reason = searchParams.get("reason");
    if (reason === "auth") {
      redirectToastShown.current = true;
      toast({ variant: "error",
        title: "Please sign in",
        description: "Your session has expired. Sign in to continue.",
        duration: 4000,
      });
      const params = new URLSearchParams(searchParams.toString());
      params.delete("reason");
      const qs = params.toString();
      router.replace(qs ? `/sign-in?${qs}` : "/sign-in");
    }
  }, [router, searchParams, toast]);

  const toastResponse = (res: signInResponse) => {
    if (res.success) {
      toast({ variant: "success",
        title: "Successful",
        description: HOME_REDIRECT,
        duration: 3000,
      });
    } else {
      toast({ variant: "error",
        title: "Failed",
        description: res.msg,
        duration: 5000,
      });
      setIsLoading(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.

    setIsLoading(true);
    try {
      const res: signInResponse = await signIn({
        email: values.email,
        password: values.password,
      });

      if (res.success && res.data) {
        setUser(res.data);
        triggerReRegistration();
      }

      toastResponse(res);

      setTimeout(() => {
        router.push(getSafeRedirect());
      }, 2100);
    } catch (error) {
      setIsLoading(false);
      toast({ variant: "error",
        title: "Error",
        description: "An unexpected error occurred.",
        duration: 4000,
      });
    }
  };

  return (
    <section className="auth-form motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-700">
      <header className="flex flex-col">
        <Link href="/" className="cursor-pointer flex items-center gap-1">
          
          <Image
            src="/images/logo-hor.svg"
            width={250}
            height={70}
            alt="Deemcee logo"
          />

          {/* <h1 className="text-26 font-ibm-plex-serif font-bold text-black-1">
            {appConfig.app_name}
          </h1> */}
        </Link>

        <div className="flex flex-col gap-1 md:gap-3">
          <h1 className="text-24 lg:text-36 font-semibold text-gray-900 dark:text-slate-100">
            {type === "sign-in" ? "Sign In" : "Sign Up"}
          </h1>
          <p className="text-16 font-normal text-gray-600 dark:text-slate-300">
            Please enter your details
          </p>
        </div>
      </header>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <div className="form-item">
                <FormLabel className="form-label">Email</FormLabel>
                <div className="flex w-full flex-col">
                  <FormControl>
                    <Input
                      id={field.name}
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      spellCheck={false}
                      placeholder="Enter your email…"
                      className="input-class"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="form-message mt-2" />
                </div>
              </div>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              
                <div className="form-item">
                  <FormLabel className="form-label">Password</FormLabel>
                  <div className="flex w-full flex-col relative">
                    <FormControl>
                      <Input
                        id={field.name}
                        placeholder="Enter your password…"
                        className="input-class pr-10" // Add padding for the icon
                        type={showPassword ? "text" : "password"} // Toggle input type
                        autoComplete="current-password"
                        {...field}
                      />
                    </FormControl>
                    <button
                      type="button"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded text-gray-500 dark:text-slate-400 transition-colors hover:text-gray-700 dark:hover:text-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bankGradient"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                    </button>
                    <FormMessage className="form-message mt-2" />
                  </div>
                </div>
            
            )}  
          />


          <div className="flex flex-col gap-4">
            <Button variant="ghost" type="submit" className="form-btn" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />{" "}
                  &nbsp;Loading…
                </>
              ) : type === "sign-in" ? (
                "Sign In"
              ) : (
                "Sign Up"
              )}
            </Button>

             {/* Forgot Password Link */}
              {type === "sign-in" && (
                <Link
                  href="/forgot-password"
                  className="text-sm text-bankGradient dark:text-blue-400 hover:underline text-center"
                >
                  Forgot Password?
                </Link>
              )}

          </div>
        </form>
      </Form>
    </section>
  );
};

export default AuthForm;
